import { useEffect, useMemo, useRef, useState } from "react";
import { http, apiError } from "@/lib/api";
import { X, Check, CalendarBlank, CaretLeft, CaretRight, Lock, LockOpen, CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * Build per-week views:
 *   [{week: 1, days: [...]}, {week: 2, days: [...]}, ...]
 * Uses program.weeks_plan if the coach set it; otherwise reuses program.days
 * for every week (single-week template, N weeks).
 */
function buildWeekViews(program) {
  if (!program) return [];
  const n = Math.max(1, Math.min(12, program.weeks || 4));
  const baseDays = program.days || [];
  const overrides = Array.isArray(program.weeks_plan) ? program.weeks_plan : null;
  if (overrides && overrides.length > 0) {
    const byWeek = new Map(overrides.map((w) => [w.week, w.days || []]));
    return Array.from({ length: n }, (_, i) => ({
      week: i + 1,
      days: byWeek.get(i + 1) || baseDays,
    }));
  }
  return Array.from({ length: n }, (_, i) => ({ week: i + 1, days: baseDays }));
}

function computeCurrentWeek(startISO, totalWeeks) {
  if (!startISO) return 1;
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return 1;
  const days = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
  const wk = Math.floor(days / 7) + 1;
  return Math.max(1, Math.min(totalWeeks, wk));
}

/**
 * Set of "week-day" keys the client has already completed. Used to gate
 * subsequent days. A key looks like `2::Day 3` (week 2 · day named "Day 3").
 * Any log with matching week_number + day_name counts as completed.
 */
function buildCompletionSet(logs) {
  const s = new Set();
  for (const l of logs || []) {
    const wk = Number(l.week_number) || 1;
    if (l.day_name) s.add(`${wk}::${l.day_name}`);
  }
  return s;
}

// LocalStorage helpers — used for both auto-save drafts and the "just unlocked"
// celebration flag. Everything is namespaced by user so switching accounts
// doesn't leak between them.
const draftKey = (programId, week, dayName) => `ll:draft:${programId}:${week}:${dayName}`;
const UNLOCK_FLAG_KEY = "ll:unlock:next";

export default function ClientProgram() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState("");
  const [logDay, setLogDay] = useState(null);
  const [activeWeek, setActiveWeek] = useState(1);
  const [unlockedName, setUnlockedName] = useState(null); // string | null — day name we JUST unlocked

  const load = async () => {
    try {
      const [{ data: prog }, { data: pastLogs }] = await Promise.all([
        http.get("/client/program"),
        http.get("/client/workout-logs"),
      ]);
      setData(prog);
      setLogs(pastLogs || []);
      const totalWeeks = prog?.program?.weeks || 1;
      const startISO = prog?.program?.start_date || prog?.assignment?.assigned_at;
      setActiveWeek(computeCurrentWeek(startISO, totalWeeks));
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Pick up the "just unlocked" flag (set by LogSessionModal after a save) so
  // we can show the celebration banner exactly once, then clear it.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(UNLOCK_FLAG_KEY);
      if (raw) {
        setUnlockedName(raw);
        localStorage.removeItem(UNLOCK_FLAG_KEY);
      }
    } catch { /* localStorage may be blocked in incognito */ }
  }, [logs.length]);

  const weekViews = useMemo(() => buildWeekViews(data?.program), [data]);
  const currentWeek = useMemo(() => {
    if (!data?.program) return 1;
    const startISO = data.program.start_date || data.assignment?.assigned_at;
    return computeCurrentWeek(startISO, data.program.weeks || 1);
  }, [data]);
  const completed = useMemo(() => buildCompletionSet(logs), [logs]);

  if (!data) return <div className="p-10 id-label id-pulse">Loading program...</div>;
  const { program, coach_notes } = data;

  if (!program) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto">
        <div className="id-label mb-3">— Your Plan —</div>
        <h1 className="id-h1 mb-10">No plan yet</h1>
        <div className="id-card text-center py-16" data-testid="no-program">
          <p className="text-zinc-400 font-manrope">Your coach hasn&apos;t set up your plan yet. Hit them up in the chat to get started.</p>
        </div>
      </div>
    );
  }

  const activeView = weekViews.find((w) => w.week === activeWeek) || weekViews[0] || { week: 1, days: [] };
  const activeDays = activeView.days || [];
  const canGoPrev = activeWeek > 1;
  const canGoNext = activeWeek < weekViews.length;

  // For each day in the active week, decide if it's completed / unlocked / locked.
  // Rule: Day 1 is always unlocked. Day N is unlocked once Day N-1 has a log
  // for this same week_number.
  const dayStates = activeDays.map((day, idx) => {
    const isComplete = completed.has(`${activeWeek}::${day.day_name}`);
    let isUnlocked = idx === 0;
    if (idx > 0) {
      const prevDay = activeDays[idx - 1];
      isUnlocked = completed.has(`${activeWeek}::${prevDay.day_name}`);
    }
    return { isComplete, isUnlocked };
  });

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="id-label mb-3">— Workout Program —</div>
      <h1 className="id-h1 mb-2" data-testid="program-name">Your Weekly Training</h1>
      <div className="text-zinc-500 font-manrope text-sm mb-6">
        {program.weeks} weeks · {activeDays.length} workout day{activeDays.length !== 1 ? "s" : ""} this week
        {program.name && program.name !== "The 7 Lazy Lean Habits" ? (
          <span className="text-zinc-600 block mt-1 italic">Plan: {program.name}</span>
        ) : null}
      </div>

      {/* "Just unlocked next workout" celebration banner — set from LogSessionModal after a save.
          Shows once per completion, then clears via useEffect above. */}
      {unlockedName && (
        <div
          className="mb-6 border border-[#FF3333] bg-gradient-to-br from-[#FF3333]/20 via-[#FF3333]/5 to-transparent p-5 relative overflow-hidden"
          data-testid="unlock-banner"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#FF3333] flex items-center justify-center shrink-0">
              <LockOpen size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <div className="id-label text-[#FF3333] mb-1 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> Session Locked In — Next One Unlocked
              </div>
              <div className="font-oswald text-2xl md:text-3xl uppercase tracking-tight leading-tight text-white">
                You&apos;ve just unlocked{" "}
                <span className="text-[#FF3333]">{unlockedName}</span>
              </div>
              <p className="text-zinc-300 font-manrope text-sm mt-1">Momentum wins. Keep it moving.</p>
            </div>
            <button
              onClick={() => setUnlockedName(null)}
              className="text-zinc-500 hover:text-white shrink-0"
              data-testid="unlock-banner-dismiss"
              aria-label="Dismiss"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}
      {program.description && <p className="text-zinc-300 font-manrope mb-6">{program.description}</p>}

      <div className="id-card mb-6" data-testid="week-navigator">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarBlank size={16} weight="bold" className="text-[#FF3333]" />
            <div className="id-label">Week {activeWeek} of {program.weeks}</div>
            {activeWeek === currentWeek && (
              <span className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#FF3333] bg-[#FF3333]/10 text-[#FF3333] px-2 py-0.5" data-testid="current-week-badge">
                This Week
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => canGoPrev && setActiveWeek(activeWeek - 1)} disabled={!canGoPrev}
              className="p-2 border border-[#262626] text-zinc-400 hover:text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="week-nav-prev" aria-label="Previous week">
              <CaretLeft size={16} weight="bold" />
            </button>
            <button onClick={() => setActiveWeek(currentWeek)}
              className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-400 px-3 py-2"
              data-testid="jump-to-current-week">
              Jump to This Week
            </button>
            <button onClick={() => canGoNext && setActiveWeek(activeWeek + 1)} disabled={!canGoNext}
              className="p-2 border border-[#262626] text-zinc-400 hover:text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="week-nav-next" aria-label="Next week">
              <CaretRight size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="week-tabs">
          {weekViews.map((w) => {
            const isActive = w.week === activeWeek;
            const isCurrent = w.week === currentWeek;
            return (
              <button key={w.week} onClick={() => setActiveWeek(w.week)}
                className={
                  "text-[10px] font-manrope uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors " +
                  (isActive
                    ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                    : isCurrent
                      ? "border-[#FF3333]/50 text-[#FF3333]"
                      : "border-[#262626] text-zinc-500 hover:text-white hover:border-zinc-500")
                }
                data-testid={`week-tab-${w.week}`}>
                W{w.week}
              </button>
            );
          })}
        </div>
      </div>

      {coach_notes && (
        <div className="id-card mb-6" data-testid="coach-notes-block">
          <div className="id-label mb-3">Habits & Nutrition · From your coach</div>
          <pre className="text-zinc-200 text-sm font-manrope whitespace-pre-wrap leading-relaxed">{coach_notes}</pre>
        </div>
      )}

      {activeDays.length === 0 ? (
        <div className="id-card text-center py-12" data-testid="empty-week">
          <p className="text-zinc-400 font-manrope">No workouts scheduled for Week {activeWeek} yet. Your coach may still be building this block.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="workout-days-list">
          {activeDays.map((day, di) => {
            const { isComplete, isUnlocked } = dayStates[di];
            return (
              <div
                key={di}
                className={
                  "id-card relative " +
                  (isComplete ? "border-[#00CC66]/40 bg-[#00CC66]/5"
                    : !isUnlocked ? "opacity-60 border-[#262626]" : "")
                }
                data-testid={`day-card-${di}`}
              >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="min-w-0">
                    <div className="id-label mb-1 flex items-center gap-2">
                      {isComplete ? (
                        <><CheckCircle size={12} weight="fill" className="text-[#00CC66]" /> <span className="text-[#00CC66]">Completed</span></>
                      ) : !isUnlocked ? (
                        <><Lock size={12} weight="fill" className="text-zinc-500" /> <span>Locked · finish Day {di} first</span></>
                      ) : (
                        <span>{day.focus || "Workout"}</span>
                      )}
                    </div>
                    <div className={"font-oswald text-2xl uppercase tracking-wide " + (!isUnlocked ? "text-zinc-500" : "")}>
                      {day.day_name}
                    </div>
                  </div>
                  {isComplete ? (
                    <button
                      onClick={() => setLogDay({ ...day, program_id: program.id, week_number: activeWeek })}
                      className="id-btn-ghost text-xs"
                      data-testid={`start-log-${di}`}
                    >
                      <Check size={14} weight="bold" /> Log Again
                    </button>
                  ) : isUnlocked ? (
                    <button
                      onClick={() => setLogDay({ ...day, program_id: program.id, week_number: activeWeek })}
                      className="id-btn-primary"
                      data-testid={`start-log-${di}`}
                    >
                      Start Session
                    </button>
                  ) : (
                    <button
                      disabled
                      className="id-btn-ghost text-xs opacity-50 cursor-not-allowed"
                      data-testid={`start-log-locked-${di}`}
                    >
                      <Lock size={14} weight="bold" /> Locked
                    </button>
                  )}
                </div>
                <ul className={"divide-y divide-[#262626] " + (!isUnlocked ? "pointer-events-none select-none" : "")}>
                  {day.exercises.map((ex, ei) => (
                    <li key={ei} className="py-3 text-sm font-manrope">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-12 md:col-span-5 font-semibold">{ex.name}</div>
                        <div className="col-span-4 md:col-span-2 text-zinc-400"><span className="id-label">Sets</span><div>{ex.sets}</div></div>
                        <div className="col-span-4 md:col-span-2 text-zinc-400"><span className="id-label">Reps</span><div>{ex.reps}</div></div>
                        <div className="col-span-4 md:col-span-3 text-zinc-400"><span className="id-label">Target</span><div>{ex.weight_target || "—"}</div></div>
                      </div>
                      {ex.notes && (
                        <div className="mt-2 ml-0 md:ml-2 text-xs text-[#FF3333]/90 border-l-2 border-[#FF3333] pl-3 font-manrope" data-testid={`ex-coach-note-${di}-${ei}`}>
                          Coach: {ex.notes}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {logDay && (
        <LogSessionModal
          day={logDay}
          pastLogs={logs}
          allWeekDays={activeDays}
          onClose={() => setLogDay(null)}
          onSaved={(nextDayName) => {
            // Set the celebration flag so the banner shows on next render.
            try {
              if (nextDayName) localStorage.setItem(UNLOCK_FLAG_KEY, nextDayName);
            } catch { /* localStorage blocked */ }
            setLogDay(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function findPreviousSession(pastLogs, dayName) {
  if (!Array.isArray(pastLogs) || !dayName) return null;
  return pastLogs.find((l) => l.day_name === dayName) || null;
}

function formatPreviousSets(prevSession, exerciseName) {
  if (!prevSession) return null;
  const ex = (prevSession.exercises || []).find((e) => e.name === exerciseName);
  if (!ex || !(ex.sets || []).length) return null;
  const sets = ex.sets.filter((s) => Number(s.reps) > 0 || Number(s.weight) > 0);
  if (!sets.length) return null;
  return sets.map((s) => `${s.reps}×${s.weight}kg`).join(" · ");
}

/**
 * LogSessionModal
 *
 * Auto-save + per-set tick-off. Every input change and every set-tick is
 * persisted to localStorage under a per-(program, week, day) key. If the phone
 * dies mid-session, reopening the workout restores the exact state — reps,
 * weights, ticks, notes, session notes. Only cleared once the user hits Save
 * (or manually clears via the "Reset" button).
 */
function LogSessionModal({ day, pastLogs, allWeekDays, onClose, onSaved }) {
  const previous = findPreviousSession(pastLogs, day.day_name);
  const dKey = draftKey(day.program_id, day.week_number || 1, day.day_name);

  // Try to hydrate from a saved draft first; fall back to a fresh empty session.
  const initial = () => {
    try {
      const raw = localStorage.getItem(dKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.exercises)) return parsed;
      }
    } catch { /* corrupted draft — start fresh */ }
    return {
      exercises: day.exercises.map((ex) => ({
        name: ex.name,
        target_reps: ex.reps,
        sets: Array.from({ length: Number(ex.sets) || 3 }, () => ({ reps: 0, weight: 0, done: false })),
        notes: "",
      })),
      notes: "",
    };
  };

  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [autoSavedAt, setAutoSavedAt] = useState(null); // "just now" indicator
  // Synchronous double-tap guard — `saving` state updates async, so a fast
  // second tap can fire before the disabled attribute renders. `savingRef`
  // catches those races and guarantees exactly-one submission.
  const savingRef = useRef(false);

  // Auto-save any change to localStorage (fires on every state edit).
  useEffect(() => {
    try {
      localStorage.setItem(dKey, JSON.stringify(state));
      setAutoSavedAt(Date.now());
    } catch { /* quota exceeded / private mode */ }
  }, [state, dKey]);

  const updateSet = (exIdx, setIdx, patch) => {
    setState((cur) => ({
      ...cur,
      exercises: cur.exercises.map((ex, i) => i !== exIdx ? ex : {
        ...ex,
        sets: ex.sets.map((s, j) => j !== setIdx ? s : { ...s, ...patch }),
      }),
    }));
  };

  const toggleSetDone = (exIdx, setIdx) => {
    setState((cur) => ({
      ...cur,
      exercises: cur.exercises.map((ex, i) => i !== exIdx ? ex : {
        ...ex,
        sets: ex.sets.map((s, j) => j !== setIdx ? s : { ...s, done: !s.done }),
      }),
    }));
  };

  // Progress meter — how many sets have been ticked off.
  const totalSets = state.exercises.reduce((a, ex) => a + ex.sets.length, 0);
  const doneSets = state.exercises.reduce((a, ex) => a + ex.sets.filter((s) => s.done).length, 0);
  const pct = totalSets ? Math.round((doneSets / totalSets) * 100) : 0;

  const save = async () => {
    if (savingRef.current) return; // race guard vs the async setSaving
    savingRef.current = true;
    setErr("");
    setSaving(true);
    try {
      await http.post("/client/workout-logs", {
        program_id: day.program_id,
        day_name: day.day_name,
        week_number: day.week_number,
        notes: state.notes,
        exercises: state.exercises.map((ex) => ({
          name: ex.name,
          notes: ex.notes,
          sets: ex.sets
            .filter((s) => Number(s.reps) > 0 || Number(s.weight) > 0)
            .map((s) => ({ reps: Number(s.reps) || 0, weight: Number(s.weight) || 0 })),
        })),
      });
      // Clear the draft — session is now on the server.
      try { localStorage.removeItem(dKey); } catch { /* noop */ }

      // Figure out the NEXT day in this week so the parent can celebrate it.
      // If they just finished the last day of the week, celebrate the week itself.
      const idx = (allWeekDays || []).findIndex((d) => d.day_name === day.day_name);
      const nextDay = idx >= 0 && idx < (allWeekDays.length - 1) ? allWeekDays[idx + 1] : null;
      const unlockName = nextDay ? nextDay.day_name : `Week ${day.week_number} Complete`;
      onSaved(unlockName);
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); savingRef.current = false; }
  };

  const resetDraft = () => {
    if (!window.confirm("Clear this session and start over? Cannot be undone.")) return;
    try { localStorage.removeItem(dKey); } catch { /* noop */ }
    setState(initial());
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="log-session-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl my-8">
        <div className="flex items-center justify-between border-b border-[#262626] px-6 py-4 gap-3">
          <div className="min-w-0">
            <div className="id-label">— Session · Week {day.week_number || 1} —</div>
            <h2 className="font-oswald text-2xl uppercase tracking-wide mt-1 truncate">Week {day.week_number || 1} · {day.day_name}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white shrink-0" data-testid="close-log-modal">
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Progress bar + auto-save indicator — reassures the client that
            their work is being saved so a dead phone doesn't erase it. */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between text-[10px] font-manrope uppercase tracking-[0.15em] mb-2">
            <span className="text-zinc-400">{doneSets}/{totalSets} sets ticked · {pct}%</span>
            <span className="text-[#00CC66] flex items-center gap-1" data-testid="autosave-indicator">
              <CheckCircle size={11} weight="fill" /> {autoSavedAt ? "Auto-saved" : "Not saved yet"}
            </span>
          </div>
          <div className="h-1 bg-[#141414] overflow-hidden">
            <div
              className="h-full bg-[#FF3333] transition-all duration-300"
              style={{ width: `${pct}%` }}
              data-testid="session-progress-bar"
            />
          </div>
        </div>

        <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto">
          {previous && (
            <div className="border border-[#FF3333]/40 bg-[#FF3333]/5 px-4 py-3 flex items-start gap-2" data-testid="previous-session-banner">
              <div className="text-[#FF3333] text-xs mt-0.5">⚡</div>
              <div>
                <div className="id-label text-[#FF3333] mb-0.5">— Last time · {new Date(previous.logged_at).toLocaleDateString()} —</div>
                <p className="text-zinc-300 text-xs font-manrope">Try to beat one number today. Even by one rep. That&apos;s how you win the week.</p>
              </div>
            </div>
          )}
          {state.exercises.map((ex, ei) => {
            const prevSetsLine = formatPreviousSets(previous, ex.name);
            return (
              <div key={ei} className="border border-[#262626] p-4" data-testid={`log-exercise-${ei}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-oswald uppercase tracking-wide">{ex.name}</div>
                  <span className="id-label text-zinc-500">Target {ex.target_reps}</span>
                </div>
                {prevSetsLine && (
                  <div className="mb-3 text-[10px] font-manrope text-zinc-500 uppercase tracking-[0.15em]" data-testid={`prev-line-${ei}`}>
                    <span className="text-[#FF3333]/80">Last time:</span> <span className="text-zinc-300">{prevSetsLine}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {ex.sets.map((s, si) => (
                    <div key={si} className={"grid grid-cols-12 gap-2 items-center " + (s.done ? "opacity-60" : "")}>
                      <div className="col-span-2 id-label">Set {si + 1}</div>
                      <input
                        type="number" min={0} value={s.reps || ""}
                        onChange={(e) => updateSet(ei, si, { reps: e.target.value })}
                        placeholder="Reps" className="id-input col-span-4"
                        disabled={s.done}
                        data-testid={`set-reps-${ei}-${si}`}
                      />
                      <input
                        type="number" min={0} step={0.5} value={s.weight || ""}
                        onChange={(e) => updateSet(ei, si, { weight: e.target.value })}
                        placeholder="Weight (kg)" className="id-input col-span-4"
                        disabled={s.done}
                        data-testid={`set-weight-${ei}-${si}`}
                      />
                      {/* Tick-off button. Big touch target for gym use. */}
                      <button
                        type="button"
                        onClick={() => toggleSetDone(ei, si)}
                        className={
                          "col-span-2 h-10 flex items-center justify-center border transition-colors " +
                          (s.done
                            ? "border-[#00CC66] bg-[#00CC66]/20 text-[#00CC66]"
                            : "border-[#333] text-zinc-500 hover:border-white hover:text-white")
                        }
                        aria-label={s.done ? `Un-tick set ${si + 1}` : `Tick set ${si + 1}`}
                        data-testid={`set-done-${ei}-${si}`}
                      >
                        <Check size={18} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  type="text"
                  value={ex.notes || ""}
                  onChange={(e) => setState((cur) => ({
                    ...cur,
                    exercises: cur.exercises.map((x, i) => i !== ei ? x : { ...x, notes: e.target.value }),
                  }))}
                  placeholder="Exercise notes — e.g. PR, left knee twinged, form felt off..."
                  className="id-input mt-2 text-sm"
                  data-testid={`exercise-notes-${ei}`}
                  maxLength={200}
                />
              </div>
            );
          })}

          <div>
            <label className="id-label block mb-2">Session notes</label>
            <textarea
              value={state.notes}
              onChange={(e) => setState((cur) => ({ ...cur, notes: e.target.value }))}
              rows={2}
              className="id-input"
              placeholder="How did it feel? RPE, soreness, anything..."
              data-testid="session-notes"
            />
          </div>

          {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333]">{err}</div>}
        </div>

        <div className="border-t border-[#262626] px-6 py-4 flex items-center justify-between gap-3">
          <button onClick={resetDraft} className="text-zinc-500 hover:text-[#FF3333] text-xs font-manrope uppercase tracking-[0.15em]" data-testid="reset-draft-button">
            Reset
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="id-btn-secondary" data-testid="cancel-log-button">Close</button>
            <button onClick={save} disabled={saving} className="id-btn-primary" data-testid="save-log-button">
              <Check size={16} weight="bold" /> {saving ? "Saving..." : "Save & Finish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
